/**
 * Utility functions for the Shot Detail Modal feature.
 * Includes:
 * - Hexbin computation and aggregation
 * - Color and size scales for visualization
 * - Shot filtering logic
 * - Court coordinate transformations
 */

import * as d3 from "d3";
import {
  hexToRgb,
  stepFromPercentile,
  stepHexOverWhite,
  tsHexFill,
} from "../../util/reportColors";
import { hexbin as d3Hexbin } from "d3-hexbin";
import {
  Shot,
  ShotFilters,
  HexbinData,
  COURT_CONSTANTS,
  getVisibleCourtLength,
  getShotRegionCategory,
} from "./types";

// ============================================================================
// Color and Size Scales
// ============================================================================

/**
 * Color scale for hexagons based on True Shooting percentage (TS%): the report scale's seven red / green buckets
 * (util/reportColors tsHexFill), shared with the report charts. TS% = PTS / (2 * attempts), free throws included.
 */
export const colorScale = (value: number): string => tsHexFill(value);

/**
 * Tier configuration for hex size scaling.
 * Total shot count determines which tier is used.
 */
interface SizeTierConfig {
  maxTotal: number; // Upper bound of this tier (exclusive)
  hideBelow: number; // Bins with fewer attempts are invisible
  minSize: number; // Floor of the output range (smallest visible hex)
  exponent: number; // Root exponent: 2=sqrt, 3=cbrt (higher = more aggressive lift)
}

const SIZE_TIERS: SizeTierConfig[] = [
  { maxTotal: 100, hideBelow: 1, minSize: 0.5, exponent: 1.5 },
  { maxTotal: 300, hideBelow: 1, minSize: 0.4, exponent: 1.75 },
  { maxTotal: 500, hideBelow: 3, minSize: 0.3, exponent: 2 },
  { maxTotal: 900, hideBelow: 5, minSize: 0.2, exponent: 2.25 },
  { maxTotal: 1500, hideBelow: 5, minSize: 0.1, exponent: 2.5 },
];

const DEFAULT_SIZE_TIER: Omit<SizeTierConfig, "maxTotal"> = {
  hideBelow: 10,
  minSize: 0.01,
  exponent: 2.75,
};

// Use 95th percentile as effective max to spread the scale across
// the bins we care about differentiating, rather than letting one or two
// outlier bins (e.g. rim) dominate the scale.
const PERCENTILE_CAP = 0.95;
// With very few bins, percentile is unreliable — fall back to absolute max.
const MIN_BINS_FOR_PERCENTILE = 8;
const MAX_SIZE = 0.99;

/**
 * Size scale for hexagon radius based on shot volume.
 * Returns a function that maps attempt counts to radius multipliers (0 to MAX_SIZE).
 *
 * Design:
 * 1. **Tiered by total sample size** — determines hideBelow threshold, min visible
 *    size, and curve aggressiveness. Small samples get generous floors; large samples
 *    allow smaller hexes and hide noise.
 *
 * 2. **Percentile-based effective max** — the 90th percentile of visible bin counts
 *    is used as the scale ceiling instead of the absolute max. Bins above this clamp
 *    to MAX_SIZE. This prevents outlier zones (e.g. rim clusters) from compressing
 *    the rest of the scale.
 *
 * 3. **Root curve scaling** — attempt counts are mapped through t^(1/exponent).
 *    Cube root (exponent=3) lifts small values into visible territory quickly
 *    while still differentiating mid-to-high volumes.
 *
 * @param binAttempts - Array of attempt counts for all bins (for percentile computation)
 * @param totalShots - Total shots in sample (determines which tier to use)
 */
export function createSizeScale(
  binAttempts: number[],
  totalShots: number,
): (attempts: number) => number {
  const tier =
    SIZE_TIERS.find((t) => totalShots <= t.maxTotal) ?? DEFAULT_SIZE_TIER;
  const { hideBelow, minSize, exponent } = tier;

  // Filter to visible bins and sort for percentile computation
  const visibleAttempts = binAttempts
    .filter((a) => a >= hideBelow)
    .sort((a, b) => a - b);

  if (visibleAttempts.length === 0) {
    return () => 0;
  }

  // Compute effective max: percentile-based when we have enough bins,
  // absolute max otherwise
  const absoluteMax = visibleAttempts[visibleAttempts.length - 1];
  let effectiveMax: number;

  if (visibleAttempts.length >= MIN_BINS_FOR_PERCENTILE) {
    const pIndex = Math.min(
      Math.floor(visibleAttempts.length * PERCENTILE_CAP),
      visibleAttempts.length - 1,
    );
    effectiveMax = visibleAttempts[pIndex];
  } else {
    effectiveMax = absoluteMax;
  }

  // Guard against degenerate case where effectiveMax equals hideBelow
  effectiveMax = Math.max(effectiveMax, hideBelow + 1);

  const range = MAX_SIZE - minSize;

  return (attempts: number): number => {
    if (attempts < hideBelow) return 0;

    // Normalize to [0, 1], clamping above effectiveMax to 1
    const t = Math.min((attempts - hideBelow) / (effectiveMax - hideBelow), 1);

    // Apply root curve: t^(1/exponent)
    const scaled = Math.pow(t, 1 / exponent);

    return minSize + scaled * range;
  };
}

// ============================================================================
// Shot Filtering
// ============================================================================

/**
 * Apply filter state to an array of shots.
 * - Multiple selections within a category = OR logic
 * - Selections across categories = AND logic
 */
export function applyFilters(shots: Shot[], filters: ShotFilters): Shot[] {
  return shots.filter((shot) => {
    // Assisted filter
    const assistedMatch = shot.assisted
      ? filters.assisted.has("assisted")
      : filters.assisted.has("unassisted");

    // Fouled filter
    const fouledMatch = shot.fouled
      ? filters.fouled.has("fouled")
      : filters.fouled.has("not_fouled");

    // Shot type filter (empty set = all; null values included only when unfiltered)
    const shotTypeMatch =
      filters.shotType.size === 0 ||
      (shot.shotType !== null && filters.shotType.has(shot.shotType));

    // Complex shot type filter (empty set = all; null values included only when unfiltered)
    const complexShotTypeMatch =
      filters.complexShotType.size === 0 ||
      (shot.complexShotType !== null && filters.complexShotType.has(shot.complexShotType));

    // Contest level filter (empty set = all; null values included only when unfiltered)
    const contestMatch =
      filters.contestLevel.size === 0 ||
      (shot.contestLevel !== null && filters.contestLevel.has(shot.contestLevel));

    // Catch and shoot filter (null = data source doesn't have this field, always pass)
    const catchAndShootMatch =
      shot.catchAndShoot === null
        ? true
        : shot.catchAndShoot
          ? filters.catchAndShoot.has("yes")
          : filters.catchAndShoot.has("no");

    // Region category filter (empty set = all; null values included only when unfiltered)
    const regionCategory = getShotRegionCategory(shot.region);
    const regionMatch =
      filters.regionCategory.size === 0 ||
      (regionCategory !== null && filters.regionCategory.has(regionCategory));

    // Shooter filter (empty set = all shooters)
    const shooterMatch =
      filters.shooter.size === 0 || filters.shooter.has(shot.shooter);

    // Passer filter (empty set = all passers)
    // When a passer is selected, only include shots where that player was the passer
    // (unassisted shots with null passer are excluded when any passer is selected)
    const passerMatch =
      filters.passer.size === 0 ||
      (shot.passer !== null && filters.passer.has(shot.passer));

    // Contester filter: uncontested shots (no contester) drop out once one is picked
    const contesterMatch =
      !filters.contester?.size ||
      (shot.contester != null && filters.contester.has(shot.contester));

    return (
      assistedMatch &&
      fouledMatch &&
      shotTypeMatch &&
      complexShotTypeMatch &&
      contestMatch &&
      catchAndShootMatch &&
      regionMatch &&
      shooterMatch &&
      passerMatch &&
      contesterMatch
    );
  });
}

/**
 * Apply filters EXCEPT for the specified category.
 * Used to compute dynamic cross-filter counts that show "how many would match if I selected this".
 */
export function applyFiltersExcept(
  shots: Shot[],
  filters: ShotFilters,
  excludeCategory: keyof ShotFilters,
): Shot[] {
  return shots.filter((shot) => {
    if (excludeCategory !== "assisted") {
      const assistedMatch = shot.assisted
        ? filters.assisted.has("assisted")
        : filters.assisted.has("unassisted");
      if (!assistedMatch) return false;
    }
    if (excludeCategory !== "fouled") {
      const fouledMatch = shot.fouled
        ? filters.fouled.has("fouled")
        : filters.fouled.has("not_fouled");
      if (!fouledMatch) return false;
    }
    if (excludeCategory !== "shotType") {
      if (filters.shotType.size > 0) {
        if (shot.shotType === null || !filters.shotType.has(shot.shotType))
          return false;
      }
    }
    if (excludeCategory !== "complexShotType") {
      if (filters.complexShotType.size > 0) {
        if (
          shot.complexShotType === null ||
          !filters.complexShotType.has(shot.complexShotType)
        )
          return false;
      }
    }
    if (excludeCategory !== "contestLevel") {
      if (filters.contestLevel.size > 0) {
        if (
          shot.contestLevel === null ||
          !filters.contestLevel.has(shot.contestLevel)
        )
          return false;
      }
    }
    if (excludeCategory !== "catchAndShoot") {
      if (shot.catchAndShoot !== null) {
        const match = shot.catchAndShoot
          ? filters.catchAndShoot.has("yes")
          : filters.catchAndShoot.has("no");
        if (!match) return false;
      }
    }
    if (excludeCategory !== "regionCategory") {
      if (filters.regionCategory.size > 0) {
        const regionCategory = getShotRegionCategory(shot.region);
        if (
          regionCategory === null ||
          !filters.regionCategory.has(regionCategory)
        )
          return false;
      }
    }
    if (excludeCategory !== "shooter") {
      if (filters.shooter.size > 0 && !filters.shooter.has(shot.shooter))
        return false;
    }
    if (excludeCategory !== "passer") {
      if (filters.passer.size > 0) {
        if (shot.passer === null || !filters.passer.has(shot.passer))
          return false;
      }
    }
    if (excludeCategory !== "contester") {
      if (filters.contester?.size) {
        if (shot.contester == null || !filters.contester.has(shot.contester))
          return false;
      }
    }
    return true;
  });
}

/**
 * Toggle a filter value within a category.
 * Prevents deselecting the last option in a category.
 */
export function toggleFilter<K extends keyof ShotFilters>(
  filters: ShotFilters,
  category: K,
  value: ShotFilters[K] extends Set<infer T> ? T : never,
): ShotFilters {
  const newSet = new Set(filters[category]) as ShotFilters[K];

  if ((newSet as Set<unknown>).has(value)) {
    // Don't allow deselecting if it's the last option
    if (newSet.size > 1) {
      (newSet as Set<unknown>).delete(value);
    }
  } else {
    (newSet as Set<unknown>).add(value);
  }

  return { ...filters, [category]: newSet };
}

/**
 * Calculate summary statistics for filtered shots.
 * FG% = makes / (makes + missed non-fouled) - excludes missed fouled shots from denominator
 * TS% = total points / total shots / 2
 */
export function calculateSummaryStats(shots: Shot[]): {
  total: number;
  makes: number;
  fga: number;
  fgPct: number;
  ptsScored: number;
  tsPct: number;
  assisted: number;
  assistedPct: number;
} {
  const total = shots.length;
  const makes = shots.filter((s) => s.made).length;
  const missedNotFouled = shots.filter((s) => !s.made && !s.fouled).length;
  const fga = makes + missedNotFouled; // FGA excludes missed fouled shots
  const ptsScored = shots.reduce((sum, s) => sum + s.ptsScored, 0);
  const assisted = shots.filter((s) => s.assisted && s.made).length;

  return {
    total,
    makes,
    fga,
    fgPct: fga > 0 ? makes / fga : 0,
    ptsScored,
    tsPct: total > 0 ? ptsScored / total / 2 : 0,
    assisted,
    assistedPct: makes > 0 ? assisted / makes : 0,
  };
}

// ============================================================================
// Hexbin Computation
// ============================================================================

/**
 * Court coordinate transformer.
 * Converts raw shot (x, y) coordinates to SVG coordinates.
 */
export function createCoordinateTransformer(
  svgWidth: number,
  svgHeight: number,
) {
  const visibleCourtLength = getVisibleCourtLength();
  const scaleX = svgWidth / COURT_CONSTANTS.COURT_WIDTH;
  const scaleY = svgHeight / visibleCourtLength;

  return {
    /**
     * Transform shot x-coordinate to SVG x-coordinate.
     */
    translateX: (x: number): number => x * scaleX,

    /**
     * Transform shot y-coordinate to SVG y-coordinate: baseline (y=0) at the top, as the report's shot charts draw
     * it (Trevor 2026-10-27; drawCourt flips its lines to match).
     */
    translateY: (y: number): number => y * scaleY,

    scaleX,
    scaleY,
  };
}

/**
 * Compute hexbin aggregation from shot data.
 * Returns an array of HexbinData objects with aggregated shot statistics per hexagon.
 */
export function computeHexbins(
  shots: Shot[],
  svgWidth: number,
  svgHeight: number,
  hexagonRadius: number = COURT_CONSTANTS.HEXAGON_RADIUS,
): HexbinData[] {
  if (shots.length === 0) return [];

  const transformer = createCoordinateTransformer(svgWidth, svgHeight);

  // Scale hexagon radius to SVG units
  const scaledRadius = hexagonRadius * transformer.scaleX;

  // Create hexbin generator
  const hexbin = d3Hexbin<Shot>()
    .x((d: Shot) => transformer.translateX(d.x))
    .y((d: Shot) => transformer.translateY(d.y))
    .radius(scaledRadius)
    .extent([
      [0, 0],
      [svgWidth, svgHeight],
    ]);

  // Bin the shots
  const bins = hexbin(shots);

  // Helper to create position key
  const makeKey = (x: number, y: number) => `${Math.round(x)}-${Math.round(y)}`;

  // Convert to HexbinData format with aggregations
  // Note: This function returns basic stats without adjacent merging.
  // The useHexbinChart hook handles adjacent merging for the visualization.
  return bins.map((bin) => {
    const makes = bin.filter((s: Shot) => s.made).length;
    const missedNotFouled = bin.filter(
      (s: Shot) => !s.made && !s.fouled,
    ).length;
    const fga = makes + missedNotFouled;
    const attempts = bin.length;
    const ptsScored = bin.reduce(
      (sum: number, s: Shot) => sum + s.ptsScored,
      0,
    );
    const fgPct = fga > 0 ? makes / fga : 0;
    // TS% = PTS / attempts / 2
    const tsPct = attempts > 0 ? ptsScored / attempts / 2 : 0;

    return {
      key: makeKey(bin.x, bin.y),
      x: bin.x,
      y: bin.y,
      shots: [...bin],
      makes,
      attempts,
      ptsScored,
      fgPct,
      tsPct,
      // Initialize merged values with original values (merging done in hook)
      mergedAttempts: attempts,
      mergedMakes: makes,
      mergedPtsScored: ptsScored,
      mergedTsPct: tsPct,
      // Neighbor keys populated in hook
      neighborKeys: [],
    };
  });
}

// ============================================================================
// Court Drawing
// ============================================================================

/**
 * Configuration for court drawing.
 */
export interface CourtDrawConfig {
  width: number;
  height: number;
  showCollegeLine?: boolean;
}

/**
 * Draw the basketball court outline on an SVG group element.
 * Based on the existing d3.basketball-shot-chart.js implementation.
 */
export function drawCourt(
  courtGroup: any, // D3 Selection of SVGGElement
  config: CourtDrawConfig,
): void {
  const { width, height, showCollegeLine = false } = config;

  const visibleCourtLength = getVisibleCourtLength();
  const scaleX = width / COURT_CONSTANTS.COURT_WIDTH;
  const scaleY = height / visibleCourtLength;

  // Helper function to scale court coordinates
  const sx = (x: number) => x * scaleX;
  const sy = (y: number) => (visibleCourtLength - y) * scaleY;

  // Court styling
  const strokeColor = "#333";
  const strokeWidth = 1;

  // Clear any existing court elements
  courtGroup.selectAll(".court-element").remove();

  // The lines are drawn baseline-at-bottom (sy) inside one group flipped top to bottom, so the court reads
  // baseline-at-top like the report charts and the transformer's points (no text here to mirror)
  const court = courtGroup
    .append("g")
    .attr("class", "court-element court-flip")
    .attr("transform", `translate(0, ${height}) scale(1, -1)`);

  // Draw baseline
  court
    .append("line")
    .attr("class", "court-element court-baseline")
    .attr("x1", 0)
    .attr("y1", sy(0))
    .attr("x2", width)
    .attr("y2", sy(0))
    .attr("stroke", strokeColor)
    .attr("stroke-width", strokeWidth);

  // Draw the key (paint area)
  const keyWidth = COURT_CONSTANTS.KEY_WIDTH;
  const freeThrowLength = COURT_CONSTANTS.FREE_THROW_LINE_LENGTH;
  const courtWidth = COURT_CONSTANTS.COURT_WIDTH;

  court
    .append("rect")
    .attr("class", "court-element court-key")
    .attr("x", sx(courtWidth / 2 - keyWidth / 2))
    .attr("y", sy(freeThrowLength))
    .attr("width", sx(keyWidth))
    .attr("height", sy(0) - sy(freeThrowLength))
    .attr("fill", "none")
    .attr("stroke", strokeColor)
    .attr("stroke-width", strokeWidth);

  // Draw free throw circle (top half - solid)
  const ftRadius = COURT_CONSTANTS.FREE_THROW_CIRCLE_RADIUS;
  drawArc(court, {
    cx: sx(courtWidth / 2),
    cy: sy(freeThrowLength),
    radius: ftRadius * scaleX,
    startAngle: -Math.PI / 2,
    endAngle: Math.PI / 2,
    className: "court-element court-ft-circle-top",
    stroke: strokeColor,
    strokeWidth,
  });

  // Draw free throw circle (bottom half - dashed)
  drawArc(court, {
    cx: sx(courtWidth / 2),
    cy: sy(freeThrowLength),
    radius: ftRadius * scaleX,
    startAngle: Math.PI / 2,
    endAngle: (3 * Math.PI) / 2,
    className: "court-element court-ft-circle-bottom",
    stroke: strokeColor,
    strokeWidth,
    dashArray: "3,3",
  });

  // Draw restricted area arc
  const raRadius = COURT_CONSTANTS.RESTRICTED_CIRCLE_RADIUS;
  const basketY =
    COURT_CONSTANTS.BASKET_PROTRUSION_LENGTH +
    COURT_CONSTANTS.BASKET_DIAMETER / 2;
  drawArc(court, {
    cx: sx(courtWidth / 2),
    cy: sy(basketY),
    radius: raRadius * scaleX,
    startAngle: -Math.PI / 2,
    endAngle: Math.PI / 2,
    className: "court-element court-restricted-area",
    stroke: strokeColor,
    strokeWidth,
  });

  // Draw three-point line arc
  const threePointRadius = COURT_CONSTANTS.THREE_POINT_RADIUS;
  const threePointSideRadius = COURT_CONSTANTS.THREE_POINT_SIDE_RADIUS;

  // Calculate arc angle based on where the arc meets the corner
  const tpAngle = Math.acos(threePointSideRadius / threePointRadius);
  // Compute exact cutoff where arc meets corner lines
  const threePointCutoff =
    basketY + Math.sqrt(threePointRadius ** 2 - threePointSideRadius ** 2);

  drawArc(court, {
    cx: sx(courtWidth / 2),
    cy: sy(basketY),
    radius: threePointRadius * scaleX,
    startAngle: -Math.PI / 2 + tpAngle,
    endAngle: Math.PI / 2 - tpAngle,
    className: "court-element court-3pt-arc",
    stroke: strokeColor,
    strokeWidth,
  });

  // Draw three-point line corners (vertical lines)
  [1, -1].forEach((side) => {
    court
      .append("line")
      .attr("class", "court-element court-3pt-corner")
      .attr("x1", sx(courtWidth / 2 + threePointSideRadius * side))
      .attr("y1", sy(0))
      .attr("x2", sx(courtWidth / 2 + threePointSideRadius * side))
      .attr("y2", sy(threePointCutoff))
      .attr("stroke", strokeColor)
      .attr("stroke-width", strokeWidth);
  });

  // Draw college/G-League three-point line if enabled
  // Uses same structure as NBA line: arc clipped at corners + straight corner lines
  if (showCollegeLine) {
    const collegeRadius = COURT_CONSTANTS.COLLEGE_THREE_POINT_RADIUS;
    const collegeAngle = Math.acos(threePointSideRadius / collegeRadius);
    const collegeCutoff =
      basketY + Math.sqrt(collegeRadius ** 2 - threePointSideRadius ** 2);

    const collegeStyle = {
      stroke: strokeColor,
      strokeWidth: 0.75,
      dashArray: "4,2",
      opacity: 0.4,
    };

    drawArc(court, {
      cx: sx(courtWidth / 2),
      cy: sy(basketY),
      radius: collegeRadius * scaleX,
      startAngle: -Math.PI / 2 + collegeAngle,
      endAngle: Math.PI / 2 - collegeAngle,
      className: "court-element court-college-3pt-line",
      ...collegeStyle,
    });

    // Corner lines
    [1, -1].forEach((side) => {
      court
        .append("line")
        .attr("class", "court-element court-college-3pt-corner")
        .attr("x1", sx(courtWidth / 2 + threePointSideRadius * side))
        .attr("y1", sy(0))
        .attr("x2", sx(courtWidth / 2 + threePointSideRadius * side))
        .attr("y2", sy(collegeCutoff))
        .attr("stroke", collegeStyle.stroke)
        .attr("stroke-width", collegeStyle.strokeWidth)
        .attr("stroke-dasharray", collegeStyle.dashArray)
        .attr("opacity", collegeStyle.opacity);
    });
  }

  // Draw backboard
  const backboardY = COURT_CONSTANTS.BASKET_PROTRUSION_LENGTH;
  const backboardWidth = COURT_CONSTANTS.BASKET_WIDTH;
  court
    .append("line")
    .attr("class", "court-element court-backboard")
    .attr("x1", sx(courtWidth / 2 - backboardWidth / 2))
    .attr("y1", sy(backboardY))
    .attr("x2", sx(courtWidth / 2 + backboardWidth / 2))
    .attr("y2", sy(backboardY))
    .attr("stroke", strokeColor)
    .attr("stroke-width", strokeWidth * 2);

  // Draw hoop
  const hoopRadius = COURT_CONSTANTS.BASKET_DIAMETER / 2;
  court
    .append("circle")
    .attr("class", "court-element court-hoop")
    .attr("cx", sx(courtWidth / 2))
    .attr("cy", sy(basketY))
    .attr("r", hoopRadius * scaleX)
    .attr("fill", "none")
    .attr("stroke", "#f97316") // Orange color for hoop
    .attr("stroke-width", strokeWidth * 1.5);

  // Draw key marks (tick marks on side of paint)
  const keyMarkWidth = COURT_CONSTANTS.KEY_MARK_WIDTH;
  [7, 8, 11, 14].forEach((mark) => {
    [1, -1].forEach((side) => {
      court
        .append("line")
        .attr("class", "court-element court-key-mark")
        .attr("x1", sx(courtWidth / 2 + (keyWidth / 2) * side))
        .attr("y1", sy(mark))
        .attr("x2", sx(courtWidth / 2 + (keyWidth / 2 + keyMarkWidth) * side))
        .attr("y2", sy(mark))
        .attr("stroke", strokeColor)
        .attr("stroke-width", strokeWidth);
    });
  });
}

/**
 * Helper function to draw an arc path.
 */
interface ArcConfig {
  cx: number;
  cy: number;
  radius: number;
  startAngle: number;
  endAngle: number;
  className: string;
  stroke: string;
  strokeWidth: number;
  dashArray?: string;
  opacity?: number;
}

function drawArc(
  group: any, // D3 Selection of SVGGElement
  config: ArcConfig,
): void {
  const {
    cx,
    cy,
    radius,
    startAngle,
    endAngle,
    className,
    stroke,
    strokeWidth,
    dashArray,
    opacity,
  } = config;

  const arc = d3
    .arc()
    .innerRadius(radius)
    .outerRadius(radius)
    .startAngle(startAngle)
    .endAngle(endAngle);

  const path = group
    .append("path")
    .attr("class", className)
    .attr("d", arc(null as any) || "")
    .attr("transform", `translate(${cx}, ${cy})`)
    .attr("fill", "none")
    .attr("stroke", stroke)
    .attr("stroke-width", strokeWidth);

  if (dashArray) {
    path.attr("stroke-dasharray", dashArray);
  }

  if (opacity !== undefined) {
    path.attr("stroke-opacity", opacity);
  }
}

// ============================================================================
// Tooltip Formatting
// ============================================================================

/**
 * Format hexbin data for tooltip display.
 * Shows merged cluster data (hex + adjacent hexes) with TS%.
 */
export function formatTooltipText(hexbin: HexbinData): string {
  return hexTipText(hexbin.mergedMakes, hexbin.mergedAttempts, hexbin.mergedTsPct);
}

/** A hexbin's own counts, on any hex lattice (the drill-down's in pixels, the report charts' in feet). */
export interface BinCounts {
  x: number;
  y: number;
  attempts: number;
  makes: number;
  ptsScored: number;
}

export interface MergedCounts {
  key: string;
  mergedAttempts: number;
  mergedMakes: number;
  mergedPtsScored: number;
  mergedTsPct: number;
  neighborKeys: string[];
}

/**
 * Adjacent-bin merging, the one method for the drill-down and the report charts (Trevor 2026-10-27): each bin adds
 * its six lattice neighbours' attempts, makes and points, and its colour, size and hover read the merged numbers, so
 * a spot's handful of shots reads with its neighbourhood. `radius` is the lattice's hex radius in the bins' units.
 * Positions are keyed at a thousandth of a unit, so a centre plus an offset finds its neighbour despite float error.
 */
export function mergeAdjacentBins<T extends BinCounts>(bins: T[], radius: number): (T & MergedCounts)[] {
  const dx = radius * Math.sqrt(3);
  const dy = radius * 1.5;
  const offsets = [
    [dx, 0],
    [-dx, 0],
    [dx / 2, dy],
    [-dx / 2, dy],
    [dx / 2, -dy],
    [-dx / 2, -dy],
  ];
  const keyOf = (x: number, y: number) => `${Math.round(x * 1000)}-${Math.round(y * 1000)}`;
  const byKey = new Map<string, T>();
  bins.forEach((b) => byKey.set(keyOf(b.x, b.y), b));
  return bins.map((b) => {
    let attempts = b.attempts;
    let makes = b.makes;
    let pts = b.ptsScored;
    const neighborKeys: string[] = [];
    offsets.forEach(([ox, oy]) => {
      const k = keyOf(b.x + ox, b.y + oy);
      const n = byKey.get(k);
      if (n) {
        attempts += n.attempts;
        makes += n.makes;
        pts += n.ptsScored;
        neighborKeys.push(k);
      }
    });
    return {
      ...b,
      key: keyOf(b.x, b.y),
      mergedAttempts: attempts,
      mergedMakes: makes,
      mergedPtsScored: pts,
      mergedTsPct: attempts > 0 ? pts / attempts / 2 : 0,
      neighborKeys,
    };
  });
}

/** A hexbin's hover line, "12/30 (54.2% TS)": the drill-down's and the report charts' (CourtSvg). */
export function hexTipText(makes: number, attempts: number, ts: number): string {
  return `${makes}/${attempts} (${(ts * 100).toFixed(1)}% TS)`;
}

/**
 * Format contest level for display.
 */
export function formatContestLevel(level: string): string {
  const labels: Record<string, string> = {
    uncontested: "Open",
    lightly_contested: "Contested",
    heavily_contested: "Tight",
  };
  return labels[level] || formatSnakeCase(level);
}

/** Format a snake_case string to Title Case. */
export function formatSnakeCase(value: string): string {
  return value
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * Format shot type for display.
 */
export function formatShotType(type: string): string {
  return type.charAt(0).toUpperCase() + type.slice(1);
}

// ============================================================================
// Display Formatting (shared with ActionDetailModal)
// ============================================================================

/**
 * Format a percentage for display.
 * Shows "<1%" for small nonzero values.
 */
export function formatPct(value: number, total: number): string {
  if (total === 0) return "0%";
  const pct = (value / total) * 100;
  if (value > 0 && pct < 1) return "<1%";
  return `${Math.round(pct)}%`;
}

// ============================================================================
// Heat Color Scale (red -> white -> green)
// ============================================================================

/**
 * Map a value to a background + text color on the team report ramp
 * (util/reportColors): the domain [min, max] is cut like percentiles into its
 * seven buckets, min = full red, the middle white, max = full green.
 * Returns null for null/undefined input.
 */
export function getHeatColor(
  value: number | null | undefined,
  min: number,
  max: number,
): { bg: string; text: string; textShadow: string } | null {
  if (value === null || value === undefined) return null;

  const t =
    max > min ? Math.max(0, Math.min(1, (value - min) / (max - min))) : 0.5;
  const [r, g, b] = hexToRgb(stepHexOverWhite(stepFromPercentile(t * 100)));

  return {
    bg: `rgb(${r}, ${g}, ${b})`,
    text: "rgba(0,0,0,0.87)",
    textShadow: "0 0 3px white, 0 0 5px white",
  };
}

// ============================================================================
// Shot-Level Partitioning & Stats
// ============================================================================

/**
 * Compute FG% using standard FGA definition (isFga).
 * FGA excludes fouled misses (shooting fouls -> free throws, not true FGA) and missed heaves.
 * And-1s (made + fouled) still count as FGA.
 */
export function computeFgPct(
  shots: TripShot[],
): number | null {
  const makes = shots.filter((s) => s.made).length;
  const fga = shots.filter(isFga).length;
  return fga > 0 ? makes / fga : null;
}

type TripShot = { made: boolean; fouled: boolean; shotType?: string | null };

/** A shot trip on the engine's rule (lib/defs/core.py IS_TSA): every shot but a missed heave. */
export function isTsa(s: TripShot): boolean {
  return s.made || s.shotType !== "heave";
}

/** A field goal attempt (IS_FGA): a fouled miss is a trip but not an attempt, an and-one is both, a missed heave is
 * neither. */
export function isFga(s: TripShot): boolean {
  return s.made || (s.shotType !== "heave" && !s.fouled);
}

type ZonedShot = { region?: string | null; shotType: string | null; three: boolean };

/** Rim / mid as the reports draw them: the payload's `region` is core.shots.zone (update/core/rules.py RIM_RULE).
 * A shot without one (no location, or a payload from before the zone) falls back to SS's layup type. */
export function isRimShot(s: ZonedShot): boolean {
  return s.region ? s.region === "rim" : s.shotType === "layup";
}

export function isMidShot(s: ZonedShot): boolean {
  return s.region ? s.region === "short mid" || s.region === "long 2" : s.shotType !== "layup" && !s.three;
}

/**
 * Partition a Shot array by a shot filter group key.
 * Returns Map<rawValue, Shot[]> sorted by count descending.
 */
export function partitionShotsByGroup(
  shots: Shot[],
  groupKey: string,
): Map<string, Shot[]> {
  const partitions = new Map<string, Shot[]>();

  for (const shot of shots) {
    let value: string | null = null;

    switch (groupKey) {
      case "assisted":
        value = shot.assisted ? "assisted" : "unassisted";
        break;
      case "fouled":
        value = shot.fouled ? "fouled" : "not_fouled";
        break;
      case "shotType":
        if (shot.shotType === null) continue;
        value = shot.shotType;
        break;
      case "complexShotType":
        if (shot.complexShotType === null) continue;
        value = shot.complexShotType;
        break;
      case "contestLevel":
        if (shot.contestLevel === null) continue;
        value = shot.contestLevel;
        break;
      case "catchAndShoot":
        if (shot.catchAndShoot === null) continue;
        value = shot.catchAndShoot ? "yes" : "no";
        break;
      case "regionCategory": {
        const cat = getShotRegionCategory(shot.region);
        if (cat === null) continue;
        value = cat;
        break;
      }
      case "shooter":
        value = shot.shooter || null;
        break;
      case "passer":
        value = shot.passer;
        break;
      case "contester":
        value = shot.contester ?? null;
        break;
      default:
        continue;
    }

    if (value === null) continue;
    if (!partitions.has(value)) partitions.set(value, []);
    partitions.get(value)!.push(shot);
  }

  // Sort by count descending
  return new Map(
    Array.from(partitions.entries()).sort((a, b) => b[1].length - a[1].length),
  );
}

/**
 * Format a shot filter group value for display.
 */
/** Known display labels for region categories. */
const REGION_CATEGORY_LABELS: Record<string, string> = {
  rim: "Rim",
  shortMid: "Short Mid",
  long2: "Long 2",
  corner3: "Corner 3",
  atb: "ATB 3",
};

export function formatShotGroupValue(groupKey: string, value: string): string {
  switch (groupKey) {
    case "assisted":
      return value === "assisted" ? "Assisted" : "Unassisted";
    case "fouled":
      return value === "fouled" ? "Fouled" : "Not Fouled";
    case "shotType":
    case "complexShotType":
      return formatSnakeCase(value);
    case "contestLevel":
      return formatContestLevel(value);
    case "catchAndShoot":
      return value === "yes" ? "C&S" : "Off Dribble";
    case "regionCategory":
      return REGION_CATEGORY_LABELS[value] || formatSnakeCase(value);
    case "shooter":
    case "passer":
    case "contester":
      return value;
    default:
      return value;
  }
}

/** Shot filter group labels for the header. */
export const SHOT_GROUP_LABELS: Record<string, string> = {
  assisted: "Assisted",
  fouled: "Fouled",
  shotType: "Shot Type",
  complexShotType: "Complex Shot Type",
  contestLevel: "Contest Level",
  catchAndShoot: "Creation",
  regionCategory: "Region",
  shooter: "Shooter",
  passer: "Passer",
  contester: "Contester",
};

/**
 * Get display label for a shot group key.
 */
export function formatShotGroupLabel(groupKey: string): string {
  return (
    SHOT_GROUP_LABELS[groupKey] ??
    groupKey
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (c) => c.toUpperCase())
      .trim()
  );
}

// ============================================================================
// Shot-Only Row Stats (for Rich Shot Detail)
// ============================================================================

export interface ShotRowStats {
  shotCount: number;
  fgPct: number | null;
  twoPtPct: number | null;
  threePtPct: number | null;
  tsPct: number | null;
  assistedPct: number | null;
  fouledPct: number | null;
  rimRate: number;
  rimTs: number | null;
  midRate: number;
  midTs: number | null;
  threeRate: number;
  threeTs: number | null;
}

/**
 * Calculate shot-only stats for a subset of shots.
 * Used by RichShotDetailSummary rows.
 */
export function calculateShotOnlyRowStats(shots: Shot[]): ShotRowStats {
  const count = shots.length;
  if (count === 0)
    return {
      shotCount: 0,
      fgPct: null,
      twoPtPct: null,
      threePtPct: null,
      tsPct: null,
      assistedPct: null,
      fouledPct: null,
      rimRate: 0,
      rimTs: null,
      midRate: 0,
      midTs: null,
      threeRate: 0,
      threeTs: null,
    };

  const totalPts = shots.reduce((sum, s) => sum + s.ptsScored, 0);
  const tsPct = totalPts / count / 2;

  const twoPtShots = shots.filter((s) => !s.three);
  const threePtShots = shots.filter((s) => s.three);
  const twoPtPct = computeFgPct(twoPtShots);
  const threePtPct = computeFgPct(threePtShots);

  const fgPct = computeFgPct(shots);
  const makes = shots.filter((s) => s.made).length;
  const assisted = shots.filter((s) => s.assisted && s.made).length;
  const assistedPct = makes > 0 ? assisted / makes : null;
  const fouled = shots.filter((s) => s.fouled).length;
  const fouledPct = count > 0 ? fouled / count : null;

  const rimShots = shots.filter(isRimShot);
  const midShots = shots.filter(isMidShot);

  const zoneStats = (zoneShotList: Shot[]) => {
    const n = zoneShotList.length;
    const pts = zoneShotList.reduce((sum, s) => sum + s.ptsScored, 0);
    return {
      rate: count > 0 ? n / count : 0,
      ts: n > 0 ? pts / n / 2 : null,
    };
  };

  const rim = zoneStats(rimShots);
  const mid = zoneStats(midShots);
  const three = zoneStats(threePtShots);

  return {
    shotCount: count,
    fgPct,
    twoPtPct,
    threePtPct,
    tsPct,
    assistedPct,
    fouledPct,
    rimRate: rim.rate,
    rimTs: rim.ts,
    midRate: mid.rate,
    midTs: mid.ts,
    threeRate: three.rate,
    threeTs: three.ts,
  };
}

// ============================================================================
// Shot Hierarchical Breakdown
// ============================================================================

export interface ShotHierarchicalRow {
  depth: number;
  label: string;
  groupKey: string;
  stats: ShotRowStats;
  shots: Shot[];
  parentShotCount: number;
  isGroupHeader: boolean;
  rowKey: string;
}

/**
 * Build hierarchical breakdown rows from shots only.
 * Simpler than the action version since there is no action/shot mode transition.
 */
export function buildShotHierarchicalRows(
  shots: Shot[],
  groupKeys: string[],
): ShotHierarchicalRow[] {
  if (groupKeys.length === 0) return [];

  const rows: ShotHierarchicalRow[] = [];

  function recurse(
    subset: Shot[],
    remainingKeys: string[],
    depth: number,
    parentCount: number,
    pathPrefix: string,
  ) {
    if (remainingKeys.length === 0) return;

    const [currentKey, ...nextKeys] = remainingKeys;
    const partitions = partitionShotsByGroup(subset, currentKey);
    const hasChildren = nextKeys.length > 0;

    for (const [value, groupShots] of partitions) {
      const rowKey = pathPrefix
        ? `${pathPrefix}|${currentKey}:${value}`
        : `${currentKey}:${value}`;

      rows.push({
        depth,
        label: formatShotGroupValue(currentKey, value),
        groupKey: currentKey,
        stats: calculateShotOnlyRowStats(groupShots),
        shots: groupShots,
        parentShotCount: parentCount,
        isGroupHeader: hasChildren,
        rowKey,
      });

      if (hasChildren) {
        recurse(groupShots, nextKeys, depth + 1, groupShots.length, rowKey);
      }
    }
  }

  recurse(shots, groupKeys, 1, shots.length, "");
  return rows;
}
